<template>
  <div class="relative">
    <div ref="trigger" class="relative">
      <InputBase
        @focus="focus"
        @blur="blur"
        v-model="query"
        :id="props.id"
        :placeholder="localPlaceholder"
        type="search"
        autocomplete="off"
        :class="[
          noSelection
            ? 'placeholder-gray-700 dark:placeholder-gray-100'
            : 'placeholder-gray-900 dark:placeholder-white',
          'text-ellipsis pr-8 focus:placeholder-gray-400',
        ]"
      />
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1"
        :class="props.maxHeight"
      >
        <HeroiconContainer class="mr-1.5" :icon="ChevronDownIcon" />
      </div>
    </div>
    <Portal>
      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          ref="container"
          class="z-40"
          v-if="initialLoad"
          v-show="menuOpen && somethingIsShowing"
        >
          <ul
            class="absolute z-40 max-h-60 w-full min-w-[18rem] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-gray-800 ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-500 sm:min-w-[20rem] sm:text-sm"
          >
            <template v-for="option in computedOptions" :key="option.id">
              <li
                @mousedown="chooseOption(option)"
                class="group relative cursor-pointer select-none list-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white dark:text-gray-200 dark:hover:bg-indigo-800"
              >
                <slot :option="option" :selected="isSelected(option)" />
              </li>
            </template>
            <div v-if="noResultsFound" class="p-2">
              <ParagraphText>No results found</ParagraphText>
            </div>
          </ul>
        </div>
      </transition>
    </Portal>
  </div>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from 'vue'

import { ChevronDownIcon } from '@heroicons/vue/solid'
import { sameWidth, usePopper } from '../../util/hooks/usePopper'
import InputBase from '../Inputs/InputBase.vue'
import HeroiconContainer from '@/Common/Elements/HeroiconContainer.vue'
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import { Portal } from '@headlessui/vue'

const emit = defineEmits(['update:modelValue', 'search', 'blur'])

const props = defineProps({
  modelValue: {
    required: true,
  },
  options: Array,
  placeholder: {
    type: String,
    default: 'Type to search...',
  },
  maxHeight: {
    default: 'max-h-56',
  },
  id: String,
  placeholderKey: {
    type: String,
    default: 'name',
  },
  placement: {
    type: String,
    default: 'bottom-start',
  },
  multiple: Boolean,
  instant: Boolean,
  allowEmptyValue: Boolean,
})

const query = ref('')
const menuOpen = ref(false)

watch(menuOpen, () => instance?.value?.update())

watchEffect(() => {
  if (props.instant || query.value) emit('search', query.value)
})

const localPlaceholder = computed(() => {
  if (props.multiple) {
    return noSelection.value
      ? props.placeholder
      : props.modelValue.map((option) => option.label).join(', ')
  } else {
    return noSelection.value
      ? props.placeholder
      : props.modelValue[props.placeholderKey]
  }
})

const noSelection = computed(() =>
  props.multiple
    ? !props.modelValue || props.modelValue.length === 0
    : !props.modelValue
)

const noResultsFound = computed(
  () => !computedOptions.value?.length && query.value
)

const somethingIsShowing = computed(
  () => noResultsFound.value || !!computedOptions.value?.length
)
function isSelected(option) {
  if (props.multiple) {
    return props.modelValue.map((el) => el.value).indexOf(option.value) !== -1
  } else {
    return props.modelValue?.id === option.id
  }
}

const computedOptions = computed(() => {
  if (props.allowEmptyValue && props.options.length === 0 && query.value) {
    return [
      {
        id: null,
        name: query.value,
      },
    ]
  } else {
    return props.options
  }
})

function chooseOption(option) {
  query.value = ''

  if (props.multiple) {
    let index = props.modelValue.map((el) => el.value).indexOf(option.value)
    if (index === -1) {
      // Not currently selected
      emit('update:modelValue', props.modelValue.concat([option]))
    } else {
      // Currently selected, so remove
      const newValue = props.modelValue
        .slice(0, index)
        .concat(props.modelValue.slice(index + 1))
      emit('update:modelValue', newValue)
    }
  } else {
    emit('update:modelValue', option)
  }
}

// This is needed so that the initial popper positioning isn't off
// Issue was happening when SelectSearch was placed inside modal, and when using
// headlessui Portal instead of vue teleport (using headlessui Portal allows clicking in SelectSearch without closing the modal)
// , the initial popper load was happening during the modal transition and SelectSearch menu was
// slightly off until the popper was updated (by scrolling, for example).
// So the initial load is set to true the first time the SelectSearch is focused, which mounts the popper
// and is positioned correctly from the get-go.
const initialLoad = ref(false)

function focus() {
  initialLoad.value = true
  menuOpen.value = true
}

function blur() {
  if (props.allowEmptyValue && props.options.length === 0 && query.value) {
    chooseOption(computedOptions.value[0])
  }

  menuOpen.value = false
  query.value = ''

  // Some components (SelectSearchLocal) listen to blur to reset the options to default
  emit('blur')
}

const offsetOptions = computed(() => {
  if (props.placement === 'bottom-start')
    return {
      offset: [0, 10],
    }
  return {
    offset: [10, 0],
  }
})
const offset = { name: 'offset', options: offsetOptions.value }

let [trigger, container, instance] = usePopper({
  placement: props.placement,
  //strategy: 'fixed',
  modifiers: [sameWidth, offset],
})
</script>
